import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';

import { CeuValue } from 'src/app/core/_models/ceuValues';
import { Indicator } from 'src/app/core/_models/indicator';
import { Operation } from 'src/app/core/_models/operation';
import { Period } from 'src/app/core/_models/period';
import { Solution } from 'src/app/core/_models/solution';

import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { DetailedOperation } from 'src/app/core/_models/detailedOperation';
import { EnginOperation } from 'src/app/core/_models/enginOperation';
import { ProductInOut } from 'src/app/core/_models/productInOut';
import { ProductOperation } from 'src/app/core/_models/productOperation';
import { Vehicle } from 'src/app/core/_models/vehicle';
import { OperationService } from 'src/app/core/_services/operation.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SolutionService } from 'src/app/core/_services/solution.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmCancelDialogComponent } from '../shared/confirm-cancel-dialog/confirm-cancel-dialog.component';
import { DialogInputComponent } from '../shared/dialog-input/dialog-input.component';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';

@Component({
  selector: 'app-work-inspection-solutions',
  templateUrl: './work-inspection-solutions.component.html',
  styleUrls: ['./work-inspection-solutions.component.scss']
})
export class WorkInspectionSolutionsComponent implements OnInit, OnChanges {
  @ViewChild('periodTabs') periodTabs!: MatTabGroup;
  @Input() originalSolution?: Solution;
  @Input() verificationSolution?: Solution;
  @Input() allIndicators: Indicator[] = [];
  @Input() wsAppPath!: string;
  @Input() versionId?: string;

  @Output() solutionChanged = new EventEmitter<Solution>();

  selectedSolutionIndex: number = 1;
  currentSolution?: Solution;
  isEditable: boolean = true;
  selectedIndicatorIndex: number = 0;
  solutionTotalValue: number = 0;
  private consolidatedOperations?: { signature: string; operations: Operation[]; cumulatedIds: Set<string> };

  get activeIndicator(): Indicator | undefined {
    if (this.allIndicators && this.allIndicators.length > this.selectedIndicatorIndex) {
      return this.allIndicators[this.selectedIndicatorIndex];
    }
    return undefined;
  }

  constructor(
    private dialog: MatDialog,
    private operationService: OperationService,
    private projectService: ProjectService,
    private solutionService: SolutionService,
    private snackbarService: SnackbarService,
    private spinnerService: SpinnerOverlayService,
    private translate: TranslateService,
    private labelI18n: LabelI18nService
  ) {}

  ngOnInit(): void {
    this.selectedSolutionIndex = this.verificationSolution ? 1 : 0;
    this.updateCurrentSolution();
    this.recalculateAllOperationValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['originalSolution'] || changes['verificationSolution']) {
      this.initializeComponent();
    }
  }

  private initializeComponent(): void {
    this.selectedSolutionIndex = this.verificationSolution ? 1 : 0;
    this.updateCurrentSolution();
    this.recalculateAllOperationValues();
  }

  recalculateSolutionTotal(): void {
    if (!this.currentSolution?.operations || !this.activeIndicator) {
      this.solutionTotalValue = 0;
      return;
    }
    this.solutionTotalValue = this.currentSolution.operations.reduce((acc, op) => acc + this.getOperationValue(op), 0);
  }

  onSolutionTabChange(event: MatTabChangeEvent): void {
    this.selectedSolutionIndex = event.index;
    this.updateCurrentSolution();
    this.recalculateAllOperationValues();
  }

  private updateCurrentSolution(): void {
    if (this.selectedSolutionIndex === 0 && this.originalSolution) {
      this.currentSolution = this.originalSolution;
      this.isEditable = false;
      return;
    }
    this.currentSolution = this.verificationSolution ?? this.originalSolution;
    this.isEditable = !!this.verificationSolution && this.currentSolution === this.verificationSolution;
  }

  getTotalSolutionValue(): string {
    if (!this.activeIndicator) return '0';
    const total = this.solutionTotalValue;

    if (this.activeIndicator.code === 'CO') {
      return total.toLocaleString(this.labelI18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return this.labelI18n.exponential(total, 2);
  }

  getOperationValue(operation: Operation): number {
    if (!this.activeIndicator) return 0;
    const total = operation.detailedOperation?.ceuValues?.find((x: CeuValue) => x.code === this.activeIndicator!.code && x.subIndicatorCode == 'TotalCost')?.ceuValue;
    return total || 0;
  }

  onPeriodicityToggleChange(): void {
    if (!this.verificationSolution || !this.verificationSolution.id) return;
    const verification = this.verificationSolution;

    this.spinnerService.show();
    this.solutionService.update(verification).subscribe({
      next: (updatedSolution) => {
        if (verification.isTemporalityEnabled) {
          this.solutionService.get(verification.id!).subscribe({
            next: (fullSolution) => this.applyVerificationSolution(fullSolution),
            error: (err) => {
              console.error('Error fetching solution with temporalities:', err);
              this.applyVerificationSolution(updatedSolution);
            }
          });
        } else {
          this.applyVerificationSolution(updatedSolution);
        }
      },
      error: (err) => {
        verification.isTemporalityEnabled = !verification.isTemporalityEnabled;
        console.error('Error updating solution periodicity:', err);
        this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_update'));
        this.spinnerService.hide();
      }
    });
  }

  private mapPeriodsToOperations(solution: Solution): Solution {
    if (!solution.operations || !solution.solutionTemporalities) {
      return solution;
    }
    const operationMap = new Map<string, Operation>();
    solution.operations.forEach((op) => {
      op.periodId = undefined;
      if (op.id) {
        operationMap.set(op.id, op);
      }
    });

    solution.solutionTemporalities.forEach((period) => {
      if (period.listOperation) {
        period.listOperation.forEach((operationId) => {
          const operation = operationMap.get(operationId);
          // Une opération n'est présente que dans une période, ses autres présences sont des copies.
          // Un double rattachement est une anomalie de données : la première période l'emporte,
          // plutôt que la dernière lue.
          if (operation && !operation.periodId) {
            operation.periodId = period.temporalityId;
          }
        });
      }
    });

    return solution;
  }

  addNewPeriod(): void {
    if (!this.verificationSolution) return;
    const verification = this.verificationSolution;
    const dialogRef = this.dialog.open(DialogInputComponent, {
      data: {
        title: this.translate.instant('work_inspection_solutions_create_period'),
        fields: [{ key: 'name', label: this.translate.instant('work_inspection_solutions_period_name'), required: true }]
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.trim()) {
        const newPeriod: Period = {
          temporalityId: '',
          name: result.trim(),
          listOperation: []
        };

        this.spinnerService.show();
        this.solutionService.addPeriodToSolution(verification.id!, newPeriod).subscribe({
          next: (createdPeriod) => {
            verification.solutionTemporalities = verification.solutionTemporalities || [];
            verification.solutionTemporalities.push(createdPeriod);
            this.solutionChanged.emit(verification);
            this.snackbarService.open(SnackbarTypes.add, this.translate.instant('work_inspection_solutions_period_added'));
            this.spinnerService.hide();

            setTimeout(() => {
              this.periodTabs.selectedIndex = this.periodTabs._tabs.length - 1;
            });
          },
          error: (err) => {
            console.error('Error adding period:', err);
            this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_add_period'));
            this.spinnerService.hide();
          }
        });
      }
    });
  }

  editPeriod(period: Period, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.verificationSolution) return;
    const verification = this.verificationSolution;
    const dialogRef = this.dialog.open(DialogInputComponent, {
      data: {
        title: this.translate.instant('work_inspection_solutions_edit_period_name'),
        fields: [{ key: 'name', label: this.translate.instant('work_inspection_solutions_period_name'), value: period.name, required: true }]
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      const newName = result;
      if (newName && newName.trim() && newName.trim() !== period.name) {
        const periodToUpdate: Period = { ...this.syncPeriodOperations(period), name: newName.trim() };

        this.spinnerService.show();
        this.solutionService.updatePeriodOnSolution(verification.id!, period.temporalityId, periodToUpdate).subscribe({
          next: (updatedPeriod) => {
            period.name = updatedPeriod.name;
            this.solutionChanged.emit(verification);
            this.snackbarService.open(SnackbarTypes.update, this.translate.instant('work_inspection_solutions_period_renamed'));
            this.spinnerService.hide();
          },
          error: (err) => {
            console.error('Error updating period:', err);
            this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_edit_period'));
            this.spinnerService.hide();
          }
        });
      }
    });
  }

  duplicatePeriod(periodToDuplicate: Period, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.verificationSolution) return;
    const verification = this.verificationSolution;

    this.spinnerService.show();
    this.solutionService.duplicatePeriodOnSolution(verification.id!, periodToDuplicate.temporalityId).subscribe({
      next: () => {
        this.reloadVerificationSolution(verification.id!, 'work_inspection_solutions_period_duplicated');
      },
      error: (err) => {
        console.error('Error duplicating period:', err);
        this.snackbarService.openServerError(err, this.translate.instant('work_inspection_solutions_error_duplicate'));
        this.spinnerService.hide();
      }
    });
  }

  private applyVerificationSolution(solution: Solution): void {
    this.verificationSolution = this.mapPeriodsToOperations(solution);
    this.solutionChanged.emit(this.verificationSolution);
    this.updateCurrentSolution();
    this.recalculateAllOperationValues();
    this.spinnerService.hide();
  }

  private reloadVerificationSolution(solutionId: string, successMessageKey: string): void {
    this.solutionService.get(solutionId).subscribe({
      next: (fullSolution) => {
        this.applyVerificationSolution(fullSolution);
        this.snackbarService.open(SnackbarTypes.add, this.translate.instant(successMessageKey));
      },
      error: (err) => {
        console.error('Error reloading solution:', err);
        this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_update'));
        this.spinnerService.hide();
      }
    });
  }

  deletePeriod(periodToDelete: Period, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.verificationSolution) return;
    const verification = this.verificationSolution;
    const dialogRef = this.dialog.open(ConfirmCancelDialogComponent, {
      data: {
        message: this.translate.instant('work_inspection_solutions_confirm_delete_period', { name: periodToDelete.name }),
        confirmText: this.translate.instant('common_btn_delete'),
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.spinnerService.show();
        this.solutionService.deletePeriodFromSolution(verification.id!, periodToDelete.temporalityId).subscribe({
          next: () => {
            verification.operations?.forEach((op) => {
              if (op.periodId === periodToDelete.temporalityId) {
                op.periodId = undefined;
              }
            });

            verification.solutionTemporalities = verification.solutionTemporalities?.filter((p) => p.temporalityId !== periodToDelete.temporalityId);
            this.solutionChanged.emit(verification);
            this.snackbarService.open(SnackbarTypes.delete, this.translate.instant('work_inspection_solutions_period_deleted'));
            this.spinnerService.hide();
          },
          error: (err) => {
            console.error('Error deleting period:', err);
            this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_delete'));
            this.spinnerService.hide();
          }
        });
      }
    });
  }

  // Une opération et ses copies forment un groupe : la case d'une période est cochée dès qu'un
  // membre du groupe s'y trouve.
  getPeriodAssignments(operation: Operation): { [periodId: string]: string } {
    const assignments: { [periodId: string]: string } = {};

    this.getGroupOperations(operation).forEach((op) => {
      if (op.periodId && op.id) {
        assignments[op.periodId] = op.id;
      }
    });

    return assignments;
  }

  // Les cases cochées ne sont appliquées qu'à la fermeture du menu, en un seul lot : sinon la première
  // case déplacerait l'opération hors de l'onglet courant et le menu se refermerait avec son panneau.
  onPeriodsChanged(event: { operationId: string; added: string[]; removed: string[] }): void {
    if (!this.verificationSolution?.id) return;
    const verification = this.verificationSolution;
    const changedOperation = verification.operations?.find((op) => op.id === event.operationId);
    if (!changedOperation) return;

    const group = this.getGroupOperations(changedOperation);
    const periodsToPersist = new Set<string>();

    // Décocher ne supprime rien : l'opération retourne dans la liste des opérations à assigner.
    event.removed.forEach((periodId) => {
      const member = group.find((op) => op.periodId === periodId);
      if (member) {
        member.periodId = undefined;
        periodsToPersist.add(periodId);
      }
    });

    // Tant que l'opération mère n'est dans aucune période, c'est elle qu'on place ; ensuite seulement
    // on copie. Une copie déjà libre n'est jamais reprise : elles portent toutes le même nom.
    const periodsToCopyInto: string[] = [];
    event.added.forEach((periodId) => {
      const mother = group.find((op) => !op.copiedFromOperationId);
      if (mother && !mother.periodId) {
        mother.periodId = periodId;
        periodsToPersist.add(periodId);
      } else {
        periodsToCopyInto.push(periodId);
      }
    });

    this.spinnerService.show();
    const updates = [...periodsToPersist]
      .map((periodId) => verification.solutionTemporalities?.find((p) => p.temporalityId === periodId))
      .filter((period): period is Period => !!period)
      .map((period) => this.solutionService.updatePeriodOnSolution(verification.id!, period.temporalityId, { ...this.syncPeriodOperations(period) }));

    // Les copies sont créées après coup : leur rattachement est posé par le serveur, une mise à jour
    // de la période envoyée ensuite les détacherait.
    const persistPeriods: Observable<unknown> = updates.length > 0 ? forkJoin(updates) : of(null);
    persistPeriods.subscribe({
      next: () => this.createPeriodCopies(verification, event.operationId, periodsToCopyInto),
      error: (err) => {
        console.error('Error updating periods:', err);
        this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_period_change'));
        this.spinnerService.hide();
      }
    });
  }

  private createPeriodCopies(verification: Solution, operationId: string, periodIds: string[]): void {
    if (periodIds.length === 0) {
      this.finishPeriodChanges(verification);
      return;
    }

    forkJoin(periodIds.map((periodId) => this.solutionService.copyOperationsToPeriod(verification.id!, periodId, [operationId]).pipe(map((copies) => ({ periodId, copies }))))).subscribe({
      next: (results) => {
        results.forEach(({ periodId, copies }) => {
          verification.operations = [...(verification.operations || []), ...copies.map((copy) => ({ ...copy, periodId }))];
          const period = verification.solutionTemporalities?.find((p) => p.temporalityId === periodId);
          if (period) {
            period.listOperation = [...(period.listOperation || []), ...copies.map((copy) => copy.id!)];
          }
        });
        this.finishPeriodChanges(verification);
      },
      error: (err) => {
        console.error('Error copying operation into periods:', err);
        this.snackbarService.openServerError(err, this.translate.instant('work_inspection_solutions_error_update'));
        this.spinnerService.hide();
      }
    });
  }

  private finishPeriodChanges(verification: Solution): void {
    this.recalculateAllOperationValues();
    this.solutionChanged.emit(verification);
    this.snackbarService.open(SnackbarTypes.update, this.translate.instant('work_inspection_solutions_operation_assigned'));
    this.spinnerService.hide();
  }

  // Suivi en période éteint, les copies ne sont pas montrées une à une : on affiche une ligne par
  // opération de la solution initiale, quantités cumulées sur la mère et ses copies. Rien n'est fusionné
  // en base, seulement à l'affichage, donc rallumer le toggle rend les périodes intactes.
  get isConsolidatedView(): boolean {
    return this.selectedSolutionIndex !== 0 && !!this.verificationSolution && !this.verificationSolution.isTemporalityEnabled && (this.verificationSolution.operations || []).some((op) => !!op.copiedFromOperationId);
  }

  // Une copie naît avec les quantités de la solution initiale : tant que personne n'a touché à ses
  // lignes, le total annonce le prévu et non le réalisé. Le compte disparaît à mesure des ajustements.
  get operationsToAdjustCount(): number {
    if (this.selectedSolutionIndex === 0) {
      return 0;
    }
    return (this.verificationSolution?.operations || []).filter((op) => !!op.copiedFromOperationId && !op.adjustedDate).length;
  }

  periodNeedsAdjustment(periodId: string): boolean {
    return (this.verificationSolution?.operations || []).some((op) => op.periodId === periodId && !!op.copiedFromOperationId && !op.adjustedDate);
  }

  get consolidatedPeriodCount(): number {
    const assigned = (this.verificationSolution?.operations || []).map((op) => op.periodId).filter((id) => !!id);
    return new Set(assigned).size;
  }

  // Le gabarit appelle cette méthode à chaque cycle de détection : le résultat est mis en cache tant
  // que les données n'ont pas bougé. Sans ça, chaque cycle fabriquerait des objets neufs, le composant
  // d'opération verrait une entrée différente à chaque fois et relancerait ses chargements en boucle.
  getDisplayedOperations(): Operation[] {
    if (!this.isConsolidatedView) {
      this.consolidatedOperations = undefined;
      return this.currentSolution?.operations || [];
    }

    const source = this.verificationSolution?.operations || [];
    const signature = source.map((op) => `${op.id}:${op.periodId ?? ''}:${op.detailedOperation?.ceuValues?.length ?? 0}`).join('|');
    if (!this.consolidatedOperations || this.consolidatedOperations.signature !== signature) {
      this.consolidatedOperations = { signature, ...this.buildConsolidatedOperations() };
    }
    return this.consolidatedOperations.operations;
  }

  // Seule une ligne qui additionne plusieurs opérations est figée : redécouper un total réparti sur
  // trois périodes n'a pas de sens. Une opération sans copie n'affiche que la sienne, elle reste donc
  // modifiable même dans cette vue.
  isOperationEditable(operation: Operation): boolean {
    if (!this.isEditable) {
      return false;
    }
    return !this.isConsolidatedView || !this.consolidatedOperations?.cumulatedIds.has(operation.id!);
  }

  private buildConsolidatedOperations(): { operations: Operation[]; cumulatedIds: Set<string> } {
    const periodNames = new Map<string, string>();
    (this.verificationSolution?.solutionTemporalities || []).forEach((p) => periodNames.set(p.temporalityId, p.name));

    const groups = new Map<string, Operation[]>();
    (this.verificationSolution?.operations || []).forEach((op) => {
      const rootId = op.copiedFromOperationId || op.id!;
      groups.set(rootId, [...(groups.get(rootId) || []), op]);
    });

    const operations = [...groups.values()].map((members) => this.consolidateGroup(members, periodNames));
    const cumulatedIds = new Set<string>();
    [...groups.values()].forEach((members) => {
      if (members.length > 1) {
        const main = members.find((op) => !op.copiedFromOperationId) || members[0];
        cumulatedIds.add(main.id!);
      }
    });

    return { operations, cumulatedIds };
  }

  private consolidateGroup(members: Operation[], periodNames: Map<string, string>): Operation {
    const main = members.find((op) => !op.copiedFromOperationId) || members[0];
    if (members.length === 1) {
      return main;
    }

    const label = (op: Operation) => (op.periodId ? periodNames.get(op.periodId) : undefined);

    return {
      ...main,
      operationProofs: members.flatMap((op) => op.operationProofs || []),
      detailedOperation: {
        ...main.detailedOperation,
        inputProducts: this.consolidateProducts(members, (op) => op.detailedOperation?.inputProducts, label),
        outputProducts: this.consolidateProducts(members, (op) => op.detailedOperation?.outputProducts, label),
        vehicles: this.consolidateVehicles(members, label),
        ceuValues: this.consolidateCeuValues(members)
      }
    };
  }

  private consolidateProducts(members: Operation[], pick: (op: Operation) => ProductOperation[] | undefined, label: (op: Operation) => string | undefined): ProductOperation[] {
    const inherited = new Map<string, ProductOperation>();
    const added: ProductOperation[] = [];

    members.forEach((member) => {
      (pick(member) || []).forEach((product) => {
        if (!product.sourceOperationCompositionId) {
          const from = label(member);
          added.push(from ? { ...product, displayName: `${product.displayName} (${from})` } : { ...product });
          return;
        }
        const previous = inherited.get(product.sourceOperationCompositionId);
        inherited.set(product.sourceOperationCompositionId, {
          ...(previous || product),
          quantity: (previous?.quantity || 0) + (product.quantity || 0)
        });
      });
    });

    return [...inherited.values(), ...added];
  }

  private consolidateVehicles(members: Operation[], label: (op: Operation) => string | undefined): Vehicle[] {
    const inherited = new Map<string, Vehicle>();
    const added: Vehicle[] = [];

    members.forEach((member) => {
      (member.detailedOperation?.vehicles || []).forEach((vehicle) => {
        if (!vehicle.sourceMaterialCompositionId) {
          const from = label(member);
          added.push(from ? { ...vehicle, displayName: `${vehicle.displayName} (${from})` } : { ...vehicle });
          return;
        }
        const previous = inherited.get(vehicle.sourceMaterialCompositionId);
        inherited.set(vehicle.sourceMaterialCompositionId, {
          ...(previous || vehicle),
          quantity: (previous?.quantity || 0) + (vehicle.quantity || 0)
        });
      });
    });

    return [...inherited.values(), ...added];
  }

  private consolidateCeuValues(members: Operation[]): CeuValue[] {
    const summed = new Map<string, CeuValue>();

    members.forEach((member) => {
      (member.detailedOperation?.ceuValues || []).forEach((value) => {
        const key = `${value.code}|${value.subIndicatorCode}`;
        const previous = summed.get(key);
        summed.set(key, { ...(previous || value), ceuValue: (previous?.ceuValue || 0) + (value.ceuValue || 0) });
      });
    });

    return [...summed.values()];
  }

  // La racine du nom d'une copie est celle de son opération mère, et elle n'est pas modifiable : seul
  // le suffixe l'est.
  getCopyRootName(operation: Operation): string {
    if (!operation.copiedFromOperationId) {
      return '';
    }
    return this.getGroupOperations(operation).find((op) => !op.copiedFromOperationId)?.displayName || '';
  }

  private getGroupOperations(operation: Operation): Operation[] {
    const rootId = operation.copiedFromOperationId || operation.id;
    return (this.verificationSolution?.operations || []).filter((op) => (op.copiedFromOperationId || op.id) === rootId);
  }

  onOperationDeleteRequested(operation: Operation): void {
    if (!this.verificationSolution?.id || !operation.id) return;
    const solutionId = this.verificationSolution.id;
    const operationId = operation.id;

    const dialogRef = this.dialog.open(ConfirmCancelDialogComponent, {
      data: {
        message: this.translate.instant('work_inspection_solutions_confirm_delete_operation', { name: operation.displayName }),
        confirmText: this.translate.instant('common_btn_delete'),
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.spinnerService.show();
      this.operationService.delete(operationId).subscribe({
        next: () => this.reloadVerificationSolution(solutionId, 'work_inspection_solutions_operation_deleted'),
        error: (err) => {
          console.error('Error deleting operation:', err);
          this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_delete'));
          this.spinnerService.hide();
        }
      });
    });
  }

  getOperationsForPeriod(periodId?: string): Operation[] {
    if (!this.verificationSolution?.operations) {
      return [];
    }
    const operations = this.verificationSolution.operations.filter((op) => (periodId ? op.periodId === periodId : !op.periodId));
    if (!periodId) {
      return operations;
    }

    // Les opérations d'une solution remontent triées par nom : dans une période c'est l'ordre
    // d'affectation qui compte, celui de listOperation. Une opération que le serveur n'a pas encore
    // renvoyée dans cette liste vient de nous être affectée, elle passe donc en dernier.
    const order = this.verificationSolution.solutionTemporalities?.find((p) => p.temporalityId === periodId)?.listOperation || [];
    const rank = (operation: Operation) => {
      const position = order.indexOf(operation.id!);
      return position === -1 ? order.length : position;
    };
    return operations.sort((a, b) => rank(a) - rank(b));
  }

  // Aligne le listOperation d'une période sur ses opérations : celles déjà présentes gardent leur
  // rang, les nouvelles passent derrière. Le serveur applique le même ordre en base.
  private syncPeriodOperations(period: Period): Period {
    const assigned = (this.verificationSolution?.operations || []).filter((op) => op.periodId === period.temporalityId).map((op) => op.id!);
    const kept = (period.listOperation || []).filter((id) => assigned.includes(id));
    period.listOperation = [...kept, ...assigned.filter((id) => !kept.includes(id))];
    return period;
  }

  onOperationUpdated(updatedOperation: Operation): void {
    if (!this.verificationSolution?.operations) return;
    const verification = this.verificationSolution;

    const index = verification.operations!.findIndex((op) => op.id === updatedOperation.id);
    if (index > -1) {
      const oldOperationState = verification.operations![index];
      const oldPeriodId = oldOperationState.periodId;
      const newPeriodId = updatedOperation.periodId !== undefined ? updatedOperation.periodId : undefined;
      const operationToStore = { ...updatedOperation, periodId: newPeriodId };

      verification.operations![index] = operationToStore;
      this.recalculateOperationValues(operationToStore);

      if (oldPeriodId !== newPeriodId) {
        this.handlePeriodChange(operationToStore, oldPeriodId, newPeriodId);
      } else {
        this.solutionChanged.emit(verification);
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDragEnter(event: DragEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('drop-zone')) {
      target.classList.add('drag-over-active');
    }
  }

  onDragLeave(event: DragEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('drop-zone')) {
      target.classList.remove('drag-over-active');
    }
  }

  onPeriodDrop(event: DragEvent, newPeriodId?: string): void {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    if (target.classList.contains('drop-zone')) {
      target.classList.remove('drag-over-active');
    }

    const operationId = event.dataTransfer?.getData('text/plain');
    if (!operationId) return;

    const droppedOperation = this.verificationSolution?.operations?.find((op) => op.id === operationId);
    if (droppedOperation && droppedOperation.periodId !== newPeriodId) {
      const oldPeriodId = droppedOperation.periodId;
      droppedOperation.periodId = newPeriodId;
      this.handlePeriodChange(droppedOperation, oldPeriodId, newPeriodId);
    }
  }

  private handlePeriodChange(operation: Operation, oldPeriodId?: string, newPeriodId?: string) {
    if (!this.verificationSolution) return;
    const verification = this.verificationSolution;
    this.spinnerService.show();
    const updateObservables = [];

    if (oldPeriodId) {
      const oldPeriod = verification.solutionTemporalities?.find((p) => p.temporalityId === oldPeriodId);
      if (oldPeriod) {
        updateObservables.push(this.solutionService.updatePeriodOnSolution(verification.id!, oldPeriodId, { ...this.syncPeriodOperations(oldPeriod) }));
      }
    }

    if (newPeriodId) {
      const newPeriod = verification.solutionTemporalities?.find((p) => p.temporalityId === newPeriodId);
      if (newPeriod) {
        updateObservables.push(this.solutionService.updatePeriodOnSolution(verification.id!, newPeriodId, { ...this.syncPeriodOperations(newPeriod) }));
      }
    }

    if (updateObservables.length === 0) {
      this.spinnerService.hide();
      this.solutionChanged.emit(verification);
      return;
    }

    forkJoin(updateObservables).subscribe({
      next: () => {
        const message = newPeriodId ? 'work_inspection_solutions_operation_assigned' : 'work_inspection_solutions_operation_unassigned';
        this.snackbarService.open(SnackbarTypes.update, this.translate.instant(message));
        this.spinnerService.hide();
        this.solutionChanged.emit(verification);
      },
      error: (err) => {
        console.error('Error updating periods after operation move:', err);
        this.snackbarService.open(SnackbarTypes.error, this.translate.instant('work_inspection_solutions_error_period_change'));
        this.spinnerService.hide();
      }
    });
  }

  trackByOperationId(index: number, operation: Operation): string {
    return operation.id!;
  }

  recalculateAllOperationValues(): void {
    if (!this.currentSolution?.operations) return;
    this.currentSolution.operations.forEach((op) => this.recalculateOperationValues(op));
  }

  recalculateOperationValues(operation: Operation): void {
    if (!operation || !operation.id) return;

    const detailedOperation = this.setDetailedOperation(operation);

    detailedOperation.versionId = this.versionId;

    this.projectService.getProjectCost(detailedOperation).subscribe({
      next: (ceuValues: CeuValue[]) => {
        if (operation.detailedOperation) {
          operation.detailedOperation.ceuValues = ceuValues;
          this.recalculateSolutionTotal();
        }
      },
      error: (err) => console.error(`Failed to get project cost for operation ${operation.displayName}`, err)
    });
  }

  private setDetailedOperation(operation: Operation): DetailedOperation {
    const detailedOperation: DetailedOperation = {};
    const productsIn: ProductInOut[] = [];
    const productsOut: ProductInOut[] = [];
    const engins: EnginOperation[] = [];

    operation.detailedOperation?.inputProducts?.forEach((product) => {
      productsIn.push({
        distance: product.distance,
        doubleFret: product.isDoubleFreight ? 1 : 0,
        energyId: product.energyId,
        quantity: product.quantity,
        resourceId: product.productId,
        transportId: product.transportId,
        workUnit: product.workUnit,
        pipeProduct: product.pipeProduct,
        diameter: product.diameter,
        unitWeight: product.unitWeight,
        productionSite: product.productionSite,
        cableProduct: product.cableProduct,
        section: product.section,
        nbConductors: product.nbConductors
      });
    });

    operation.detailedOperation?.outputProducts?.forEach((product) => {
      productsOut.push({
        distance: product.distance,
        doubleFret: product.isDoubleFreight ? 1 : 0,
        energyId: product.energyId,
        quantity: product.quantity,
        resourceId: product.productId,
        transportId: product.transportId,
        workUnit: product.workUnit,
        pipeProduct: product.pipeProduct,
        diameter: product.diameter,
        unitWeight: product.unitWeight,
        productionSite: product.productionSite,
        cableProduct: product.cableProduct,
        section: product.section,
        nbConductors: product.nbConductors
      });
    });

    operation.detailedOperation?.vehicles?.forEach((vehicle: Vehicle) => {
      engins.push({
        resourceId: vehicle.vehicleId,
        nbDays: vehicle.quantity,
        energyId: vehicle.energyId,
        materialCompositionId: vehicle.materialCompositionId
      });
    });

    detailedOperation.engins = engins;
    detailedOperation.productsIn = productsIn;
    detailedOperation.productsOut = productsOut;
    detailedOperation.dredges = operation.detailedOperation?.dredging;
    detailedOperation.tunnelers = operation.detailedOperation?.tunneling;

    return detailedOperation;
  }
}
